/**
 * The VM size each new server of the Infrastructure page is built with, in the chosen region: the fixed RDS size and the DB size, or, where
 * the environment's subscription cannot use one there, the nearest series of the same size, offered for the user to agree to (Next waits for
 * the choice; utils/vmSizes.ts, and the backend's rules/vm_sizes.py). Read from the Infrastructure and wizard stores,
 * so every place that shows a size (the RDS VM and DB VM cards, the Review step, the Next button) asks the same question and shares one answer.
 */
import { useDeploymentWizard } from '@/stores/deploymentWizard'
import { rdsVmCount, useInfrastructureWizard } from '@/stores/infrastructureWizard'
import type { VmSizeCheck } from '@/types/infrastructure'
import { builtServers, sizeOfTier, sizeWhere, uncheckedVmSizes, vmSizeProblems } from '@/utils/vmSizes'
import { useVmSizes } from '@/hooks/useInfra'

/** What is said when the lookup itself failed (no answer from the backend), so a failed check is never shown as no check. */
export const CHECK_FAILED_NOTE = 'The portal could not ask Azure just now.'

export interface VmSizeCheckResult {
  /** One per new server that is built (none when both servers are Existing or the product does not use the pool tiers). */
  sizes: VmSizeCheck[]
  rds?: VmSizeCheck
  db?: VmSizeCheck
  /** True when Azure was asked. */
  checked: boolean
  /** True when the subscription's vCPU quota was read too: without it, an available size is not confirmed against the quota. */
  quotaChecked: boolean
  /** Why it could not be, when it could not: what the backend said, or that the question itself failed. Never silent. */
  note: string
  isLoading: boolean
  /** Ask Azure again (the quota may have been raised since). */
  recheck: () => void
  /** True while the check is being asked, the first time or again. */
  isChecking: boolean
  /** Sizes nothing can replace, and replacements the user has not agreed to. Next does not continue while there are any. */
  problems: string[]
}

/**
 * `hosts`: how many RDS VMs are built; each needs its own vCPU quota. Left out, it is the number of RDS VMs the user chose (`rdsVmCount`),
 * which is what every caller means: a caller that passes a number must pass that one, or two cards would ask Azure two different questions.
 */
export function useVmSizeCheck(hosts?: number): VmSizeCheckResult {
  const product = useInfrastructureWizard((s) => s.product)
  const level = useInfrastructureWizard((s) => s.level)
  const rdsServer = useInfrastructureWizard((s) => s.rdsServer)
  const dbServer = useInfrastructureWizard((s) => s.dbServer)
  const chosenHosts = rdsVmCount({ product, rdsServer })
  const environmentId = useInfrastructureWizard((s) => s.environment?.id ?? '')
  const regionId = useInfrastructureWizard((s) => s.region?.id ?? '')
  const environment = useInfrastructureWizard((s) => s.environment)
  const region = useInfrastructureWizard((s) => s.region)
  const choices = useInfrastructureWizard((s) => s.vmSizeChoices)
  const poolTier = useDeploymentWizard((s) => s.poolTier)

  const built = builtServers({ product, level, rdsServer, dbServer })
  const wanted = (built.rds || built.db) && environmentId !== '' && regionId !== ''
  const asked = hosts ?? chosenHosts
  const query = useVmSizes(wanted ? { environmentId, regionId, poolTier, ...built, hosts: asked } : null)

  // Until the answer comes (or when it cannot), the sizes are the catalog's: what the page shows.
  const sizes = wanted ? (query.data?.sizes ?? uncheckedVmSizes({ poolTier, ...built, hosts: asked })) : []
  return {
    sizes,
    rds: sizeOfTier(sizes, 'rds'),
    db: sizeOfTier(sizes, 'db'),
    checked: query.data?.checked ?? false,
    quotaChecked: query.data?.quotaChecked ?? false,
    note: query.data?.note ?? (wanted && query.isError ? CHECK_FAILED_NOTE : ''),
    isLoading: wanted && query.isLoading,
    recheck: () => void query.refetch(),
    isChecking: wanted && query.isFetching,
    problems: vmSizeProblems(sizes, choices, sizeWhere({ environment, region })),
  }
}
